import { useEffect, useRef, useState } from "react";
import {
  RECOGNITION_DEFAULT_THRESHOLD,
  RECOGNITION_DEFAULT_HOLD_MS,
} from "@ndp61/cms-shared";
import { FIELD_CAPTURE_DEFAULT_HOLD_MS } from "../constants";
import { loadPoiSeed, findNearestPoi } from "../lib/poi-seed";
import type { PoiEntry } from "../lib/poi-seed";

interface Props {
  onSubmit(data: { poiId: string; threshold: number; holdMs: number }): void;
}

type SeedState = "loading" | "loaded" | "missing";

type GpsState =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "found"; lat: number; lng: number }
  | { status: "failed" };

function validatePoiId(value: string): string | null {
  if (value.trim() === "") return "POI ID is required";
  if (/\s/.test(value)) return "POI ID must not contain spaces";
  return null;
}

export function MetadataEditor({ onSubmit }: Props) {
  const [seedState, setSeedState] = useState<SeedState>("loading");
  const [pois, setPois] = useState<PoiEntry[]>([]);
  const [selectedPoiId, setSelectedPoiId] = useState("");
  const [manualPoiId, setManualPoiId] = useState("");
  const [threshold, setThreshold] = useState(RECOGNITION_DEFAULT_THRESHOLD);
  const [holdMs, setHoldMs] = useState(FIELD_CAPTURE_DEFAULT_HOLD_MS);
  const [touched, setTouched] = useState(false);
  const [gps, setGps] = useState<GpsState>({ status: "idle" });
  const hasPreSelected = useRef(false);
  const watchIdRef = useRef<number | null>(null);

  // Load POI list from the seed file dropped into public/.
  useEffect(() => {
    loadPoiSeed().then((entries) => {
      if (entries.length > 0) {
        setPois(entries);
        setSeedState("loaded");
      } else {
        setSeedState("missing");
      }
    });
  }, []);

  // Background GPS watch — pre-selection only, never blocks form interaction.
  useEffect(() => {
    if (!navigator.geolocation) {
      setGps({ status: "failed" });
      return;
    }
    setGps({ status: "checking" });

    const startWatch = () => {
      if (watchIdRef.current !== null || hasPreSelected.current) return;
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          setGps({ status: "found", lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {
          setGps((prev) => (prev.status === "checking" ? { status: "failed" } : prev));
        },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
      );
    };

    const stopWatch = () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopWatch();
      } else {
        startWatch();
      }
    };

    onVisibilityChange();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopWatch();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  // Pre-select nearest POI once both GPS and POI list are ready.
  useEffect(() => {
    if (hasPreSelected.current) return;
    if (gps.status !== "found" || seedState !== "loaded" || pois.length === 0) return;
    if (selectedPoiId !== "") return;
    const nearest = findNearestPoi(pois, gps.lat, gps.lng);
    if (nearest) {
      setSelectedPoiId(nearest.poi.poiId);
      hasPreSelected.current = true;
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    }
  }, [gps, pois, selectedPoiId, seedState]);

  // Validation varies by mode.
  const dropdownError =
    touched && seedState === "loaded" && selectedPoiId === "" ? "Select a location" : null;
  const manualError =
    touched && seedState === "missing" ? validatePoiId(manualPoiId) : null;
  const isValid =
    seedState === "loading"
      ? false
      : seedState === "loaded"
        ? selectedPoiId !== ""
        : validatePoiId(manualPoiId) === null;

  // GPS distance hint: shown when the dropdown selection matches the GPS-nearest POI.
  const nearestByGps =
    gps.status === "found" && pois.length > 0
      ? findNearestPoi(pois, gps.lat, gps.lng)
      : null;
  const distanceHint =
    nearestByGps?.poi.poiId === selectedPoiId && nearestByGps
      ? `GPS: ~${Math.round(nearestByGps.distM)} m away`
      : null;

  const selectedPoi =
    selectedPoiId !== "" ? pois.find((p) => p.poiId === selectedPoiId) ?? null : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!isValid) return;
    const poiId = seedState === "loaded" ? selectedPoiId : manualPoiId.trim();
    onSubmit({ poiId, threshold, holdMs });
  }

  return (
    <form onSubmit={handleSubmit} style={s.form}>
      <h2 style={s.heading}>Session Metadata</h2>

      <div style={s.field}>
        <label htmlFor="poiId" style={s.label}>
          Location (POI)
          {seedState === "loading" && <span style={s.loadingTag}> · loading…</span>}
          {gps.status === "checking" && seedState !== "loading" && (
            <span style={s.gpsChecking}> · detecting GPS…</span>
          )}
        </label>

        {seedState === "loading" && (
          <div style={s.skeleton} aria-busy="true" aria-label="Loading POI list" />
        )}

        {seedState === "loaded" && (
          <>
            <select
              id="poiId"
              value={selectedPoiId}
              onChange={(e) => setSelectedPoiId(e.target.value)}
              onBlur={() => setTouched(true)}
              style={{ ...s.select, ...(dropdownError ? s.fieldError : {}) }}
              aria-describedby={distanceHint ? "gps-hint" : undefined}
            >
              <option value="">— select a location —</option>
              {pois.map((p) => (
                <option key={p.poiId} value={p.poiId}>
                  {p.poiId} — {p.nameEn}
                </option>
              ))}
            </select>
            {distanceHint && (
              <p id="gps-hint" style={s.gpsHint}>
                📍 {distanceHint}
              </p>
            )}
            {gps.status === "failed" && (
              <p style={s.gpsWarning}>GPS unavailable — select manually</p>
            )}
            {dropdownError && <p style={s.errorText}>{dropdownError}</p>}
            {selectedPoi && selectedPoi.descriptionEn !== "" && (
              <p style={s.description}>{selectedPoi.descriptionEn}</p>
            )}
          </>
        )}

        {seedState === "missing" && (
          <>
            <input
              id="poiId"
              type="text"
              value={manualPoiId}
              onChange={(e) => setManualPoiId(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="e.g. 1"
              style={{ ...s.input, ...(manualError ? s.fieldError : {}) }}
            />
            {manualError && <p style={s.errorText}>{manualError}</p>}
          </>
        )}
      </div>

      <div style={s.field}>
        <label htmlFor="threshold" style={s.label}>
          Recognition Threshold:{" "}
          <span style={s.labelValue}>{threshold.toFixed(2)}</span>
          <span style={s.labelMeta}> (default: {RECOGNITION_DEFAULT_THRESHOLD.toFixed(2)})</span>
        </label>
        <input
          id="threshold"
          type="range"
          min={0.3}
          max={0.95}
          step={0.01}
          value={threshold}
          onChange={(e) => setThreshold(parseFloat(e.target.value))}
          style={s.range}
        />
      </div>

      <div style={{ ...s.field, marginBottom: "2rem" }}>
        <label htmlFor="holdMs" style={s.label}>
          Hold Duration:{" "}
          <span style={s.labelValue}>{holdMs} ms</span>
          <span style={s.labelMeta}> (default: {RECOGNITION_DEFAULT_HOLD_MS} ms)</span>
        </label>
        <input
          id="holdMs"
          type="range"
          min={100}
          max={5000}
          step={100}
          value={holdMs}
          onChange={(e) => setHoldMs(parseInt(e.target.value, 10))}
          style={s.range}
        />
      </div>

      <button
        type="submit"
        disabled={touched && !isValid}
        style={{ ...s.btn, ...(touched && !isValid ? s.btnDisabled : {}) }}
      >
        {seedState === "loading" ? "Loading…" : "Continue"}
      </button>
    </form>
  );
}

const s: Record<string, React.CSSProperties> = {
  form: {
    padding: "1rem",
    maxWidth: 480,
    margin: "0 auto",
  },
  heading: {
    marginBottom: "1.5rem",
    fontSize: 17,
    fontWeight: 700,
    color: "#111",
  },
  field: {
    marginBottom: "1.25rem",
  },
  label: {
    display: "block",
    marginBottom: "0.25rem",
    fontWeight: 600,
    fontSize: 14,
  },
  labelValue: {
    fontWeight: 400,
  },
  labelMeta: {
    marginLeft: "0.5rem",
    color: "#666",
    fontSize: "0.85em",
    fontWeight: 400,
  },
  loadingTag: {
    fontWeight: 400,
    color: "#888",
    fontSize: "0.85em",
  },
  gpsChecking: {
    fontWeight: 400,
    color: "#888",
    fontSize: "0.85em",
  },
  skeleton: {
    height: 44,
    borderRadius: 6,
    background: "#f0f0f0",
    width: "100%",
  },
  select: {
    width: "100%",
    padding: "0.5rem 0.75rem",
    fontSize: "1rem",
    borderRadius: 6,
    border: "1px solid #ccc",
    boxSizing: "border-box" as const,
    background: "#fff",
    appearance: "auto" as const,
    minHeight: 44,
  },
  input: {
    width: "100%",
    padding: "0.5rem 0.75rem",
    fontSize: "1rem",
    borderRadius: 6,
    border: "1px solid #ccc",
    boxSizing: "border-box" as const,
    minHeight: 44,
  },
  fieldError: {
    border: "2px solid #c0392b",
  },
  range: {
    width: "100%",
  },
  gpsHint: {
    margin: "0.25rem 0 0",
    fontSize: 12,
    color: "#2d7a2d",
  },
  gpsWarning: {
    margin: "0.25rem 0 0",
    fontSize: 12,
    color: "#888",
  },
  description: {
    margin: "0.5rem 0 0",
    padding: "0.625rem 0.75rem",
    background: "#f7f7f7",
    borderLeft: "3px solid #c8102e",
    borderRadius: 4,
    fontSize: 13,
    lineHeight: 1.45,
    color: "#333",
  },
  errorText: {
    color: "#c0392b",
    marginTop: "0.25rem",
    fontSize: "0.875rem",
  },
  btn: {
    width: "100%",
    padding: "0.75rem",
    fontSize: "1rem",
    borderRadius: 6,
    border: "none",
    background: "#0057b7",
    color: "#fff",
    cursor: "pointer",
    minHeight: 44,
  },
  btnDisabled: {
    background: "#ccc",
    cursor: "not-allowed",
  },
};
